import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Text, Separator } from '@radix-ui/themes';
import { useAuth } from '../../hooks/useAuth';
import { useNotifications } from '../../hooks/useNotifications';
import {
  HomeIcon,
  CalendarDaysIcon,
  HeartIcon,
  StarIcon,
  CreditCardIcon,
  BellIcon,
  UserCircleIcon,
  Cog6ToothIcon,
  QuestionMarkCircleIcon,
  InformationCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChartBarIcon,
  ScissorsIcon,
  ClockIcon,
  CurrencyDollarIcon,
  ChatBubbleLeftRightIcon,
  PhotoIcon,
  UsersIcon,
  BuildingStorefrontIcon,
  TagIcon,
  GiftIcon,
  DocumentTextIcon,
  WrenchScrewdriverIcon,
  Squares2X2Icon,
  FunnelIcon,
  ArrowLeftOnRectangleIcon,
} from '@heroicons/react/24/outline';

const clientNavItems = [
  { section: 'Main', items: [
    { name: 'Dashboard', path: '/dashboard', icon: HomeIcon },
    { name: 'My Bookings', path: '/bookings', icon: CalendarDaysIcon },
    { name: 'Favorites', path: '/favorites', icon: HeartIcon },
    { name: 'My Reviews', path: '/my-reviews', icon: StarIcon },
  ]},
  { section: 'Payments', items: [
    { name: 'Payment Methods', path: '/payment-methods', icon: CreditCardIcon },
    { name: 'Transactions', path: '/transactions', icon: DocumentTextIcon },
  ]},
  { section: 'Account', items: [
    { name: 'Notifications', path: '/notifications', icon: BellIcon },
    { name: 'My Profile', path: '/profile', icon: UserCircleIcon },
    { name: 'Settings', path: '/settings', icon: Cog6ToothIcon },
  ]},
  { section: 'Support', items: [
    { name: 'Help Center', path: '/help', icon: QuestionMarkCircleIcon },
    { name: 'About Us', path: '/about', icon: InformationCircleIcon },
  ]},
];

const providerNavItems = [
  { section: 'Overview', items: [
    { name: 'Dashboard', path: '/provider/dashboard', icon: HomeIcon },
    { name: 'Bookings', path: '/provider/bookings', icon: CalendarDaysIcon },
  ]},
  { section: 'Business', items: [
    { name: 'Manage Services', path: '/provider/services', icon: ScissorsIcon },
    { name: 'Availability', path: '/provider/availability', icon: ClockIcon },
    { name: 'My Earnings', path: '/provider/earnings', icon: CurrencyDollarIcon },
  ]},
  { section: 'Engagement', items: [
    { name: 'Reviews & Ratings', path: '/provider/reviews', icon: StarIcon },
    { name: 'Analytics', path: '/provider/analytics', icon: ChartBarIcon },
    { name: 'Messages', path: '/provider/messages', icon: ChatBubbleLeftRightIcon },
  ]},
  { section: 'Profile', items: [
    { name: 'Business Profile', path: '/provider/profile', icon: BuildingStorefrontIcon },
    { name: 'Portfolio', path: '/provider/portfolio', icon: PhotoIcon },
    { name: 'Settings', path: '/provider/settings', icon: Cog6ToothIcon },
  ]},
  { section: 'Support', items: [
    { name: 'Help Center', path: '/help', icon: QuestionMarkCircleIcon },
  ]},
];

const adminNavItems = [
  { section: 'Overview', items: [
    { name: 'Dashboard', path: '/admin/dashboard', icon: HomeIcon },
    { name: 'Analytics', path: '/admin/analytics', icon: ChartBarIcon },
  ]},
  { section: 'Management', items: [
    { name: 'Manage Users', path: '/admin/users', icon: UsersIcon },
    { name: 'Manage Providers', path: '/admin/providers', icon: BuildingStorefrontIcon },
    { name: 'Manage Bookings', path: '/admin/bookings', icon: CalendarDaysIcon },
  ]},
  { section: 'Content', items: [
    { name: 'Categories', path: '/admin/categories', icon: Squares2X2Icon },
    { name: 'Promotions', path: '/admin/promotions', icon: GiftIcon },
    { name: 'Content', path: '/admin/content', icon: DocumentTextIcon },
  ]},
  { section: 'System', items: [
    { name: 'Settings', path: '/admin/settings', icon: WrenchScrewdriverIcon },
    { name: 'Policies', path: '/admin/policies', icon: TagIcon },
  ]},
];

const publicNavItems = [
  { section: 'Explore', items: [
    { name: 'Home', path: '/', icon: HomeIcon },
    { name: 'Find Services', path: '/search', icon: Squares2X2Icon },
  ]},
];

const Sidebar = ({ onFilterChange, initialFilters = {} }) => {
  const { isAuthenticated, isProvider, isAdmin, logout } = useAuth();
  const { unreadCount } = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  const [filters, setFilters] = useState({
    location: initialFilters.location || '',
    minPrice: initialFilters.minPrice || '',
    maxPrice: initialFilters.maxPrice || '',
    rating: initialFilters.rating || '',
    reviews: initialFilters.reviews || '',
    mobile: initialFilters.mobile || '',
    ...initialFilters,
  });

  const isActive = (path) => location.pathname === path;

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    if (onFilterChange) onFilterChange(newFilters);
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => { if (v) params.set(k, v); });
    navigate(`/search?${params.toString()}`);
  };

  const clearFilters = () => {
    const emptyFilters = { location: '', minPrice: '', maxPrice: '', rating: '', reviews: '', mobile: '' };
    setFilters(emptyFilters);
    if (onFilterChange) onFilterChange(emptyFilters);
    navigate('/search');
  };

  const navItems = isAdmin ? adminNavItems : isProvider ? providerNavItems : isAuthenticated ? clientNavItems : publicNavItems;

  const filterInputStyle = {
    width: '100%',
    padding: '6px 10px',
    fontSize: '13px',
    borderRadius: '6px',
    border: '1px solid var(--gray-6)',
    backgroundColor: 'var(--color-background)',
    color: 'var(--gray-12)',
    outline: 'none',
  };

  return (
    <aside
      style={{
        width: isCollapsed ? '64px' : '300px',
        minWidth: isCollapsed ? '64px' : '300px',
        borderRight: '1px solid var(--gray-4)',
        backgroundColor: 'var(--color-background)',
        transition: 'width 0.25s ease, min-width 0.25s ease',
        position: 'relative',
        flexShrink: 0,
        zIndex: 30,
      }}
    >
      {/* Collapse toggle */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        style={{
          position: 'absolute',
          right: '-12px',
          top: '24px',
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          backgroundColor: 'var(--orange-9)',
          color: '#fff',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
        }}
      >
        {isCollapsed
          ? <ChevronRightIcon style={{ width: '14px', height: '14px' }} />
          : <ChevronLeftIcon style={{ width: '14px', height: '14px' }} />
        }
      </button>

      <nav className="h-full overflow-y-auto py-4 px-2">
        {navItems.map((section, idx) => (
          <div key={section.section} style={{ marginTop: idx > 0 ? '20px' : 0 }}>
            {!isCollapsed && (
              <Text
                size="1"
                weight="bold"
                style={{
                  display: 'block',
                  padding: '0 10px',
                  marginBottom: '6px',
                  color: 'var(--gray-9)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  textAlign: 'left',
                }}
              >
                {section.section}
              </Text>
            )}
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {section.items.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                const badge = item.path === '/notifications' && unreadCount > 0
                  ? (unreadCount > 99 ? '99+' : unreadCount)
                  : null;
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      title={isCollapsed
                        ? (badge ? `${item.name} (${badge} unread)` : item.name)
                        : undefined}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: isCollapsed ? '8px' : '8px 10px',
                        borderRadius: '8px',
                        justifyContent: isCollapsed ? 'center' : 'flex-start',
                        textDecoration: 'none',
                        fontSize: '14px',
                        fontWeight: active ? '600' : '400',
                        backgroundColor: active ? 'var(--orange-9)' : 'transparent',
                        color: active ? '#fff' : 'var(--gray-11)',
                        borderLeft: active ? '3px solid var(--orange-11)' : '3px solid transparent',
                        transition: 'background-color 0.15s ease, color 0.15s ease',
                        position: 'relative',
                      }}
                      onMouseEnter={e => {
                        if (!active) {
                          e.currentTarget.style.backgroundColor = 'var(--orange-3)';
                          e.currentTarget.style.color = 'var(--orange-11)';
                        }
                      }}
                      onMouseLeave={e => {
                        if (!active) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = 'var(--gray-11)';
                        }
                      }}
                    >
                      <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                        <Icon style={{ width: '18px', height: '18px' }} />
                        {badge && isCollapsed && (
                          <span
                            style={{
                              position: 'absolute',
                              top: '-6px',
                              right: '-8px',
                              minWidth: '16px',
                              height: '16px',
                              padding: '0 4px',
                              borderRadius: '999px',
                              backgroundColor: 'var(--red-9, #e5484d)',
                              color: '#fff',
                              fontSize: '10px',
                              fontWeight: 700,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '2px solid var(--color-background)',
                              lineHeight: 1,
                            }}
                          >
                            {badge}
                          </span>
                        )}
                      </span>
                      {!isCollapsed && (
                        <>
                          <span style={{ flex: 1 }}>{item.name}</span>
                          {badge && (
                            <span
                              style={{
                                minWidth: '20px',
                                height: '20px',
                                padding: '0 6px',
                                borderRadius: '999px',
                                backgroundColor: active ? 'rgba(255,255,255,0.25)' : 'var(--red-9, #e5484d)',
                                color: '#fff',
                                fontSize: '11px',
                                fontWeight: 700,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                lineHeight: 1,
                                flexShrink: 0,
                              }}
                            >
                              {badge}
                            </span>
                          )}
                        </>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        {/* Filters — public pages only */}
        {!isAuthenticated && !isProvider && !isAdmin &&
          (location.pathname === '/' || location.pathname === '/search') && (
          <div style={{ marginTop: '20px' }}>
            <Separator style={{ margin: '0 4px 12px' }} />
            <button
              onClick={() => setShowFilters(!showFilters)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                padding: '8px 10px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                color: 'var(--gray-11)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FunnelIcon style={{ width: '16px', height: '16px', color: 'var(--orange-9)' }} />
                {!isCollapsed && <Text size="2" weight="medium" style={{ color: 'var(--gray-12)', textAlign: 'left' }}>Filters</Text>}
              </div>
              {!isCollapsed && (showFilters
                ? <ChevronUpIcon style={{ width: '14px', height: '14px' }} />
                : <ChevronDownIcon style={{ width: '14px', height: '14px' }} />
              )}
            </button>

            {showFilters && !isCollapsed && (
              <div style={{ padding: '4px 6px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Budget */}
                <div>
                  <Text size="1" weight="bold" style={{ color: 'var(--gray-9)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '6px', textAlign: 'left' }}>
                    Budget
                  </Text>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <input type="number" placeholder="Min Price" value={filters.minPrice}
                      onChange={e => handleFilterChange('minPrice', e.target.value)} style={filterInputStyle} />
                    <input type="number" placeholder="Max Price" value={filters.maxPrice}
                      onChange={e => handleFilterChange('maxPrice', e.target.value)} style={filterInputStyle} />
                  </div>
                </div>

                {/* Rating */}
                <div>
                  <Text size="1" weight="bold" style={{ color: 'var(--gray-9)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '6px', textAlign: 'left' }}>
                    Rating
                  </Text>
                  <select value={filters.rating} onChange={e => handleFilterChange('rating', e.target.value)} style={filterInputStyle}>
                    <option value="">Any Rating</option>
                    <option value="5">5 Stars</option>
                    <option value="4.5">4.5+ Stars</option>
                    <option value="4">4+ Stars</option>
                    <option value="3">3+ Stars</option>
                  </select>
                </div>

                {/* Service Type */}
                <div>
                  <Text size="1" weight="bold" style={{ color: 'var(--gray-9)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '6px', textAlign: 'left' }}>
                    Service Type
                  </Text>
                  <select value={filters.mobile} onChange={e => handleFilterChange('mobile', e.target.value)} style={filterInputStyle}>
                    <option value="">All Types</option>
                    <option value="true">Mobile Service</option>
                    <option value="false">In-Shop Only</option>
                  </select>
                </div>

                {/* Location */}
                <div>
                  <Text size="1" weight="bold" style={{ color: 'var(--gray-9)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '6px', textAlign: 'left' }}>
                    Location
                  </Text>
                  <input type="text" placeholder="Enter location" value={filters.location}
                    onChange={e => handleFilterChange('location', e.target.value)} style={filterInputStyle} />
                </div>

                <button
                  onClick={clearFilters}
                  style={{ width: '100%', padding: '6px', fontSize: '13px', fontWeight: '500', color: 'var(--orange-9)', background: 'none', border: 'none', cursor: 'pointer', borderRadius: '6px' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--orange-3)'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  Clear All Filters
                </button>
              </div>
            )}

            {/* Special Offers */}
            {!isCollapsed && (
              <div style={{ marginTop: '16px' }}>
                <Separator style={{ margin: '0 4px 12px' }} />
                <Text
                  size="1"
                  weight="bold"
                  style={{
                    display: 'block',
                    padding: '0 6px',
                    marginBottom: '8px',
                    color: 'var(--gray-9)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    textAlign: 'left',
                  }}
                >
                  Special Offers
                </Text>
                <div style={{ padding: '0 4px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {[
                    { img: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=300&h=160&fit=crop', discount: '30% OFF', title: 'Full Spa Day Package' },
                    { img: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=300&h=160&fit=crop', discount: '20% OFF', title: 'Hair Styling Makeover' },
                    { img: 'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=300&h=160&fit=crop', discount: '25% OFF', title: 'Premium Nail Art' },
                  ].map(({ img, discount, title }) => (
                    <Link
                      key={title}
                      to="/search"
                      style={{
                        display: 'block',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        textDecoration: 'none',
                        border: '1px solid var(--gray-4)',
                        backgroundColor: 'var(--gray-2)',
                        transition: 'box-shadow 0.15s ease',
                      }}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)'}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                    >
                      <div style={{ position: 'relative', height: '80px', overflow: 'hidden' }}>
                        <img src={img} alt={title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <span style={{
                          position: 'absolute', top: '6px', right: '6px',
                          backgroundColor: 'var(--orange-9)', color: '#fff',
                          fontSize: '10px', fontWeight: 700, padding: '2px 6px', borderRadius: '10px',
                        }}>
                          {discount}
                        </span>
                      </div>
                      <div style={{ padding: '6px 8px' }}>
                        <Text size="1" weight="medium" style={{ color: 'var(--gray-12)', display: 'block', textAlign: 'left' }}>
                          {title}
                        </Text>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {/* Logout — authenticated users only */}
        {isAuthenticated && (
          <div style={{ marginTop: '24px', padding: '0 4px' }}>
            <Separator style={{ margin: '0 4px 12px' }} />
            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to log out?')) {
                  logout();
                  navigate('/login');
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: isCollapsed ? '8px' : '8px 10px',
                borderRadius: '8px',
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                width: '100%',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                color: 'var(--red-9, #e5484d)',
                transition: 'background-color 0.15s ease',
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--red-2, #fff5f5)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              title={isCollapsed ? 'Logout' : undefined}
            >
              <ArrowLeftOnRectangleIcon style={{ width: '18px', height: '18px', flexShrink: 0 }} />
              {!isCollapsed && <span>Logout</span>}
            </button>
          </div>
        )}
      </nav>
    </aside>
  );
};

export default Sidebar;
